/**
 * useActivePlane — Surface Layer (/01)
 *
 * Holds the explicit active plane (DevHub vs Workspace) for identities that can
 * enter more than one plane, and resolves the *effective* plane for rendering.
 *
 * Source of truth precedence:
 *   1. an explicit user switch (persisted, session-scoped) — when still allowed
 *   2. otherwise the deterministic default derived from roles
 *
 * Zustand rule: selectors return PRIMITIVES only (a `HubPlane | null` string, an
 * `id` string). The `?? EMPTY_ROLES` fallback lives OUTSIDE the selector so the
 * store never hands back a fresh reference (the documented render-loop trap).
 */

import { create } from 'zustand';
import { useAuthStore } from '@auth/auth-store';
import { canAccessPlane, defaultPlaneFor, type HubPlane } from './hubPlane';

const STORAGE_KEY = 'surface.shell.activePlane.v1';

function readStored(): HubPlane | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'devhub' || raw === 'userhub' ? raw : null;
  } catch {
    return null;
  }
}

function writeStored(plane: HubPlane | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (plane) window.localStorage.setItem(STORAGE_KEY, plane);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Active plane is a UX preference cache only; never throws into routing.
  }
}

interface ActivePlaneStore {
  /** null = follow the role-derived default; a value = explicit user choice. */
  activePlane: HubPlane | null;
  setActivePlane: (plane: HubPlane) => void;
  resetActivePlane: () => void;
}

export const useActivePlaneStore = create<ActivePlaneStore>((set) => ({
  activePlane: readStored(),
  setActivePlane: (plane) => {
    writeStored(plane);
    set({ activePlane: plane });
  },
  resetActivePlane: () => {
    writeStored(null);
    set({ activePlane: null });
  },
}));

/** Stable empty reference — keeps the roles selector from returning a fresh ref. */
const EMPTY_ROLES: readonly string[] = [];

/**
 * The effective plane to render for the current identity: the explicit choice
 * when the user may still access it, otherwise the deterministic default.
 */
export function useResolvedPlane(): HubPlane {
  const roles = useAuthStore((s) => s.identity?.roles) ?? EMPTY_ROLES;
  const chosen = useActivePlaneStore((s) => s.activePlane);
  if (chosen && canAccessPlane(roles, chosen)) return chosen;
  return defaultPlaneFor(roles);
}
